import EvidenceFiles from '../sales/EvidenceFiles';
import PropertyCorrectionDetails from './PropertyCorrectionDetails';
import LotProductionFields from './LotProductionFields';
import '../sales/PurchaserFields.css';
import { purchaserName } from '../../lib/purchaser';
import { useConfirmation } from '../../context/ConfirmationContext';
import { useModalDialog } from '../ui/useModalDialog';
import React, { useState, useEffect, useCallback, useRef, useId } from 'react';
import { createPortal } from 'react-dom';
import './LotDetailDrawer.css';
import PolygonEditor from './PolygonEditor';
import type { Point } from './polygon';
import mapIcon from '@iconify/icons-solar/map-point-bold-duotone';
import notesIcon from '@iconify/icons-solar/notes-bold-duotone';
import historyIcon from '@iconify/icons-solar/history-bold-duotone';
import overviewIcon from '@iconify/icons-solar/widget-bold-duotone';
import transactionIcon from '@iconify/icons-solar/hand-money-bold-duotone';
import settingsIcon from '@iconify/icons-solar/settings-bold-duotone';
import closeIcon from '@iconify/icons-solar/close-circle-bold-duotone';
import lockIcon from '@iconify/icons-solar/lock-bold-duotone';
import { Icon } from '@iconify/react';
import { Button, Divider } from '../ui';
import type { Lot, LotStatus, Section, LotAuditLog } from '../../types/inventory';
import { STATUS_CONFIG, CATEGORY_CONFIG, fmt, formatDate } from '../../types/inventory';
import { api } from '../../lib/api';

interface TransactionContext {
  sale: any | null;
  ledgerEntries: any[];
  auditLogs: any[];
}

interface LotDetailDrawerProps {
  lot: Lot | null;
  section?: Section;
  auditLogs: LotAuditLog[];
  auditError?: string;
  onClose: () => void;
  onStatusChange: (lotId: string, newStatus: LotStatus, reason?: string) => Promise<boolean>;
  onPriceChange: (lotId: string, newPrice: number) => Promise<boolean>;
  onLockToggle: (lotId: string, lock: boolean, reason?: string) => Promise<boolean>;
  onAddNote: (lotId: string, note: string) => Promise<boolean>;
  onDelete?: (lotId: string) => void;
  onReposition?: (lotId: string, data: { x_coord: number; y_coord: number; width?: number; height?: number }) => Promise<boolean>;
  onShapeChange?: (lotId: string, points: Point[] | null) => Promise<boolean>;
  onTransactionComplete?: () => void;
  onPropertySaved?: () => void;
}

const ALLOWED_TRANSITIONS: Record<string, LotStatus[]> = {
  AVAILABLE: ['MAINTENANCE', 'LOCKED', 'BLOCKED'],
  RESERVED: [],
  SOLD: [],
  MAINTENANCE: ['AVAILABLE'],
  LOCKED: ['AVAILABLE'],
  BLOCKED: ['AVAILABLE'],
};

const LotDetailDrawer: React.FC<LotDetailDrawerProps> = ({
  lot, section, auditLogs, auditError, onClose, onStatusChange, onPriceChange, onLockToggle, onAddNote, onDelete, onReposition, onShapeChange, onTransactionComplete, onPropertySaved,
}) => {
  const { confirmAction } = useConfirmation();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [closing, setClosing] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'actions' | 'history' | 'transaction'>('info');
  const [newPrice, setNewPrice] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [noteText, setNoteText] = useState('');
  const [actionSaving, setActionSaving] = useState(false);
  const [actionError, setActionError] = useState('');
  const [showPriceEdit, setShowPriceEdit] = useState(false);
  const [showShapeEdit, setShowShapeEdit] = useState(false);
  const [shapeDraft, setShapeDraft] = useState<Point[] | null>(lot?.points || null);
  const [shapeValid, setShapeValid] = useState(true);
  const [showPosEdit, setShowPosEdit] = useState(false);
  const [posForm, setPosForm] = useState({ x: 0, y: 0, w: 0, h: 0 });

  // Transaction state
  const [txCtx, setTxCtx] = useState<TransactionContext | null>(null);
  const [txLoading, setTxLoading] = useState(false);
  const [txSubmitting, setTxSubmitting] = useState(false);
  const [txError, setTxError] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [agents, setAgents] = useState<any[]>([]);
  const [reserveForm, setReserveForm] = useState({
    agentId: '', saleAmount: '', paymentType: 'INSTALLMENT', downPayment: '', installmentMonths: '12', notes: '',
  });

  useModalDialog(dialogRef, true);
  useEffect(() => { contentRef.current?.scrollTo({ top: 0 }); }, [activeTab]);
  useEffect(() => {
    if (!closing) return;
    const timer = window.setTimeout(onClose, 180);
    return () => window.clearTimeout(timer);
  }, [closing, onClose]);
  const requestClose = () => {
    if (actionSaving || txSubmitting) return;
    const hasDraft = showShapeEdit || showPriceEdit || showPosEdit || noteText.trim() || statusReason.trim() || selectedClient || clientSearch.trim() || reserveForm.agentId || reserveForm.saleAmount || reserveForm.downPayment || reserveForm.notes || reserveForm.paymentType !== 'INSTALLMENT' || reserveForm.installmentMonths !== '12';
    if (hasDraft && !window.confirm('Discard unsaved changes and close this lot?')) return;
    setClosing(true);
  };

  // Fetch transaction context when lot changes or tab switches to transaction
  const fetchTxContext = useCallback(async () => {
    if (!lot) return;
    setTxLoading(true);
    setTxError('');
    try {
      const ctx = await api.inventory.transactions.context(lot.id);
      setTxCtx(ctx);
    } catch { setTxCtx(null); setTxError('Transaction details could not be loaded. Reopen this tab to try again.'); }
    setTxLoading(false);
  }, [lot]);

  useEffect(() => {
    if (activeTab === 'transaction' && lot) {
      fetchTxContext();
      api.inventory.transactions.agents().then(setAgents).catch(() => {});
    }
  }, [activeTab, lot, fetchTxContext]);

  // Client search debounce
  useEffect(() => {
    if (clientSearch.length < 2) { setClientResults([]); return; }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const results = await api.inventory.transactions.searchClients(clientSearch);
        if (!cancelled) setClientResults(results);
      } catch { setClientResults([]); }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [clientSearch]);

  // Transaction handlers
  const handleReserve = async () => {
    if (!lot || !selectedClient || !reserveForm.agentId || !reserveForm.saleAmount) {
      setTxError('Select a client, agent, and enter sale amount');
      return;
    }
    setTxLoading(true);
    setTxSubmitting(true);
    setTxError('');
    try {
      await api.inventory.transactions.reserve(lot.id, {
        clientId: selectedClient.id,
        agentId: reserveForm.agentId,
        saleAmount: Number(reserveForm.saleAmount),
        paymentType: reserveForm.paymentType,
        downPayment: Number(reserveForm.downPayment) || 0,
        installmentMonths: Number(reserveForm.installmentMonths) || 12,
        notes: reserveForm.notes || undefined,
      });
      fetchTxContext();
      onTransactionComplete?.();
    } catch (e: any) {
      setTxError(e.message || 'Failed to reserve');
    }
    setTxLoading(false);
    setTxSubmitting(false);
  };

  const handleRelease = async () => {
    if (!lot) return;
    await confirmAction({ title: `Release lot ${lot.lotNumber}?`, description: 'This cancels the reservation’s sale and makes the lot available again. Payments must be resolved before a reservation can be released.', confirmLabel: 'Release reservation', pendingLabel: 'Releasing…', successTitle: 'Reservation released', errorTitle: 'Reservation not released', action: async () => {
      setTxSubmitting(true); setTxError('');
      try { await api.inventory.transactions.release(lot.id, { reason: 'Released via map' }); fetchTxContext(); onTransactionComplete?.(); }
      finally { setTxSubmitting(false); }
    }});
  };

  const handleCompleteSale = async () => {
    if (!lot) return;
    await confirmAction({ title: `Complete sale for lot ${lot.lotNumber}?`, description: 'This completes the sale and marks the lot as sold. All outstanding payments must be settled first.', confirmLabel: 'Complete sale', pendingLabel: 'Completing…', successTitle: 'Sale completed', errorTitle: 'Sale not completed', action: async () => {
      setTxSubmitting(true); setTxError('');
      try { await api.inventory.transactions.completeSale(lot.id); fetchTxContext(); onTransactionComplete?.(); }
      finally { setTxSubmitting(false); }
    }});
  };

  if (!lot) return null;

  const config = STATUS_CONFIG[lot.status];
  const catConfig = section ? CATEGORY_CONFIG[section.category] : null;
  const transitions = ALLOWED_TRANSITIONS[lot.status] || [];
  const lotLogs = auditLogs.filter(l => l.lotId === lot.id);

  const runAction = async (action: () => Promise<boolean>) => {
    if (actionSaving) return false;
    setActionSaving(true); setActionError('');
    try {
      const saved = await action();
      if (!saved) setActionError('The change was not saved. Check the details and try again.');
      return saved;
    } catch { setActionError('The change was not saved. Try again.'); return false; }
    finally { setActionSaving(false); }
  };
  const handleStatusChange = async (newStatus: LotStatus) => {
    if (await runAction(() => onStatusChange(lot.id, newStatus, statusReason || undefined))) setStatusReason('');
  };
  const handlePriceSave = async () => {
    const price = Number(newPrice);
    if (!Number.isFinite(price) || price < 0 || !newPrice.trim()) { setActionError('Enter a valid price of zero or more.'); return; }
    if (await runAction(() => onPriceChange(lot.id, price))) { setShowPriceEdit(false); setNewPrice(''); }
  };
  const handleAddNote = async () => {
    if (noteText.trim() && await runAction(() => onAddNote(lot.id, noteText.trim()))) setNoteText('');
  };

  const tabs = [
    { key: 'info', label: 'Overview', icon: overviewIcon },
    { key: 'transaction', label: 'Transaction', icon: transactionIcon },
    { key: 'actions', label: 'Manage', icon: settingsIcon },
    { key: 'history', label: 'Activity', icon: historyIcon },
  ] as const;

  const statusDescription = lot.isLocked ? 'This lot is locked and cannot be reserved until it is unlocked.' : {
    AVAILABLE: 'This lot is available. Start a reservation to link a client and create a sale.',
    RESERVED: 'A reservation is recorded for this lot. Review the client, payment balance, and documents.',
    SOLD: 'This lot is sold. Open its transaction to review the sale and available documents.',
    MAINTENANCE: 'This lot is under maintenance and is currently unavailable for reservation.',
    LOCKED: 'This lot is locked and currently unavailable for reservation.',
    BLOCKED: 'This lot is blocked and currently unavailable for reservation.',
  }[lot.status];
  const goToTransaction = !lot.isLocked && ['AVAILABLE', 'RESERVED', 'SOLD'].includes(lot.status);

  return createPortal(
    <dialog ref={dialogRef} className={`lot-sheet${closing ? ' is-closing' : ''}`} aria-labelledby={titleId}
      onKeyDown={event => {
        if (event.key !== 'Tab') return;
        const focusable = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]'))
          .filter(element => element.tabIndex >= 0 && element.getClientRects().length > 0 && !element.closest('fieldset:disabled'));
        const first = focusable[0], last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }}
      onCancel={event => { event.preventDefault(); requestClose(); }}
      onClick={event => { if (event.target === event.currentTarget) requestClose(); }}>
      <div className="lot-sheet-shell">
        <header className="lot-sheet-header">
          <div className="lot-sheet-title-row">
            <h2 id={titleId}>Lot {lot.lotNumber}</h2>
            <span className={`lot-sheet-status status-${lot.status.toLowerCase()}`}><span style={{ background: config.color }} />{config.label}</span>
            <button type="button" className="lot-sheet-close" onClick={requestClose} disabled={actionSaving || txSubmitting} aria-label="Close lot details" autoFocus><Icon icon={closeIcon} width={24} /></button>
          </div>
          <p className="lot-sheet-location"><Icon icon={mapIcon} width={18} />{section?.label || lot.sectionId}<span>·</span>{section?.phase || 'Phase not specified'}</p>
          <div className="lot-sheet-summary">
            <div><span>Listed price</span><strong>{fmt(lot.price)}</strong></div>
            <div><span>Lot type</span><strong>{lot.lotType?.name || 'Standard'}</strong></div>
            <div><span>Capacity</span><strong>{lot.lotType?.capacity || 1} <small>{(lot.lotType?.capacity || 1) === 1 ? 'interment' : 'interments'}</small></strong></div>
          </div>
        </header>
        <div className="lot-sheet-tabs" role="tablist" aria-label="Lot information">
          {tabs.map((tab, index) => <button key={tab.key} type="button" role="tab" id={`${titleId}-${tab.key}`}
            aria-selected={activeTab === tab.key} aria-controls={`${titleId}-panel`} tabIndex={activeTab === tab.key ? 0 : -1}
            onClick={() => setActiveTab(tab.key)} onKeyDown={event => {
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
              setActiveTab(tabs[next].key);
              document.getElementById(`${titleId}-${tabs[next].key}`)?.focus();
            }}><Icon icon={tab.icon} width={19} />{tab.label}</button>)}
        </div>
        <div ref={contentRef} className="lot-sheet-content" role="tabpanel" id={`${titleId}-panel`} aria-labelledby={`${titleId}-${activeTab}`} tabIndex={0}>
          {activeTab === 'info' && <div className="lot-overview">
            <LotProductionFields lot={lot} onSaved={() => onPropertySaved?.()} />
            <EvidenceFiles key={`evidence-${lot.id}`} kind="lots" ownerId={lot.id} />
            <section className="lot-next-step">
              <Icon icon={lot.isLocked ? lockIcon : transactionIcon} width={24} />
              <div><h3>{lot.isLocked ? 'Locked for reservation' : config.label === 'Available' ? 'Ready for reservation' : `${config.label} lot`}</h3><p>{statusDescription}</p>
                {lot.lockedReason && <p><strong>Reason:</strong> {lot.lockedReason}</p>}
                {lot.expiryDate && <p><strong>Reservation expires:</strong> {formatDate(lot.expiryDate)}</p>}
                <Button variant="primary" size="sm" onClick={() => setActiveTab(goToTransaction ? 'transaction' : 'actions')}>
                  {goToTransaction ? lot.status === 'AVAILABLE' ? 'Start reservation' : 'View transaction' : 'Manage availability'}
                </Button>
              </div>
            </section>
            <section className="lot-overview-section">
              <h3><Icon icon={mapIcon} width={20} />Location & specifications</h3>
              <dl className="lot-specifications">
                {[
                  ['Section', section?.label || lot.sectionId],
                  ['Category', catConfig?.label || section?.category || 'Not specified'],
                  ['Position', lot.isFreeform ? `Custom placement · X ${Math.round(lot.x_coord)}, Y ${Math.round(lot.y_coord)}` : `Row ${lot.row + 1}, Column ${lot.col + 1}`],
                  ['Orientation', ({ N: 'North', S: 'South', E: 'East', W: 'West', NE: 'Northeast', NW: 'Northwest', SE: 'Southeast', SW: 'Southwest' } as Record<string, string>)[lot.orientation] || lot.orientation || 'Not specified'],
                  ['Outline', lot.points?.length ? `Polygon · ${lot.points.length} points` : 'Rectangle'],
                  ['Layout', section?.layoutMode ? section.layoutMode.charAt(0) + section.layoutMode.slice(1).toLowerCase() : 'Grid'],
                  ['Created', formatDate(lot.createdAt)],
                ].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
              </dl>
            </section>
            {lot.lotType?.imageUrl && <figure className="lot-type-photo"><img src={lot.lotType.imageUrl} alt={lot.lotType.name} /><figcaption>{lot.lotType.name} · Lot type reference</figcaption></figure>}
            {lot.status === 'SOLD' && <p><a href={`/sales/property/${lot.id}/cases`}>Contractual rights and interment exceptions</a></p>}
            {lot.status === 'SOLD' && lot.occupantName && <section className="lot-overview-section"><h3><Icon icon={overviewIcon} width={20} />Interment record</h3><dl className="lot-specifications"><div><dt>Occupant</dt><dd>{lot.occupantName}</dd></div><div><dt>Interment date</dt><dd>{formatDate(lot.intermentDate)}</dd></div></dl></section>}
            <section className="lot-overview-section">
              <div className="lot-section-heading"><h3><Icon icon={notesIcon} width={20} />Notes</h3><button className="lot-text-button" onClick={() => setActiveTab('actions')}>Add a note</button></div>
              <p className="lot-notes">{lot.notes || 'No notes yet. Add context for the team in Manage.'}</p>
            </section>
          </div>}

        {/* TRANSACTION TAB */}
        {activeTab === 'transaction' && (
          <div className="lot-transaction" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="lot-content-heading"><h3>Transaction</h3><p>Client, payment details, and sale documents for this lot.</p></div>
            {txLoading && (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '16px', fontSize: '13px' }}>
                <Icon icon="solar:refresh-bold-duotone" width={16} style={{ animation: 'spin 1s linear infinite', marginRight: '6px' }} />
                Loading...
              </div>
            )}

            {txError && (
              <div style={{
                padding: '8px 12px', borderRadius: '6px', background: 'rgba(239,68,68,0.10)',
                color: '#EF4444', fontSize: '13px', fontWeight: 600,
              }}>{txError}</div>
            )}

            {lot.isLocked && <p className="inventory-error">This lot is locked. Unlock it before starting a reservation.</p>}
            {/* ── AVAILABLE: Reserve Form ────────────── */}
            {lot.status === 'AVAILABLE' && !lot.isLocked && !txLoading && txCtx !== null && (
              <>
                <div style={{ fontSize: '15px', color: 'var(--text-primary)', fontWeight: 600, textTransform: 'none', letterSpacing: 'normal' }}>
                  Reserve for Client
                </div>

                {/* Client search */}
                <label className="lot-field-label" htmlFor="lot-client-search">Client</label>
                <div style={{ position: 'relative' }}>
                  {selectedClient ? (
                    <div style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 10px', borderRadius: '6px', background: 'rgba(13,148,136,0.10)',
                      border: '1px solid rgba(13,148,136,0.25)',
                    }}>
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>
                          {purchaserName(selectedClient)}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{selectedClient.phone}</div>
                      </div>
                      <button aria-label="Change selected client" onClick={() => { setSelectedClient(null); setClientSearch(''); }}
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '2px' }}>
                        <Icon icon="solar:close-circle-bold-duotone" width={14} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <input id="lot-client-search" aria-label="Search client by name or phone" placeholder="Search client by name or phone"
                        value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                        style={{
                          width: '100%', background: 'var(--input-bg)',
                          border: '1px solid var(--input-border)', borderRadius: '6px',
                          padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px',
                          fontFamily: 'var(--font-body)', outline: 'none',
                        }} />
                      {clientResults.length > 0 && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                          background: 'var(--modal-bg)', border: '1px solid var(--border-base)',
                          borderRadius: '0 0 6px 6px', maxHeight: '160px', overflowY: 'auto',
                        }}>
                          {clientResults.map(c => (
                            <button key={c.id} onClick={() => { setSelectedClient(c); setClientResults([]); setClientSearch(''); }}
                              style={{
                                display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
                                background: 'none', border: 'none', borderBottom: '1px solid var(--border-base)',
                                cursor: 'pointer', color: 'var(--text-primary)', fontSize: '13px',
                                fontFamily: 'var(--font-body)',
                              }}>
                              <span style={{ fontWeight: 600 }}>{purchaserName(c)}</span>
                              <span style={{ color: 'var(--text-muted)', marginLeft: '8px' }}>{c.phone}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Agent */}
                <label className="lot-field-label" htmlFor="lot-reservation-agent">Assigned agent</label>
                <select id="lot-reservation-agent" value={reserveForm.agentId} onChange={e => setReserveForm(f => ({ ...f, agentId: e.target.value }))}
                  style={{
                    width: '100%', background: 'var(--input-bg)',
                    border: '1px solid var(--input-border)', borderRadius: '6px',
                    padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px',
                    fontFamily: 'var(--font-body)', outline: 'none',
                  }}>
                  <option value="">Select Agent...</option>
                  {agents.map(a => <option key={a.id} value={a.id}>{a.name} ({a.role})</option>)}
                </select>

                {/* Sale amount (pre-fill with lot price) */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  <div>
                    <label htmlFor="lot-reserve-amount" style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600 }}>Sale Amount</label>
                    <input id="lot-reserve-amount" type="number" placeholder={String(lot.price)}
                      value={reserveForm.saleAmount} onChange={e => setReserveForm(f => ({ ...f, saleAmount: e.target.value }))}
                      onFocus={() => { if (!reserveForm.saleAmount) setReserveForm(f => ({ ...f, saleAmount: String(lot.price) })); }}
                      style={{
                        width: '100%', background: 'var(--input-bg)',
                        border: '1px solid var(--input-border)', borderRadius: '6px',
                        padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px',
                        fontFamily: 'var(--font-body)', outline: 'none',
                      }} />
                  </div>
                  <div>
                    <label htmlFor="lot-reserve-downpayment" style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600 }}>Down Payment</label>
                    <input id="lot-reserve-downpayment" type="number" placeholder="0"
                      value={reserveForm.downPayment} onChange={e => setReserveForm(f => ({ ...f, downPayment: e.target.value }))}
                      style={{
                        width: '100%', background: 'var(--input-bg)',
                        border: '1px solid var(--input-border)', borderRadius: '6px',
                        padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px',
                        fontFamily: 'var(--font-body)', outline: 'none',
                      }} />
                  </div>
                </div>

                {/* Payment type + months */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                  <div>
                    <label htmlFor="lot-reserve-payment" style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600 }}>Payment Type</label>
                    <select id="lot-reserve-payment" value={reserveForm.paymentType} onChange={e => setReserveForm(f => ({ ...f, paymentType: e.target.value }))}
                      style={{
                        width: '100%', background: 'var(--input-bg)',
                        border: '1px solid var(--input-border)', borderRadius: '6px',
                        padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px',
                        fontFamily: 'var(--font-body)', outline: 'none',
                      }}>
                      <option value="INSTALLMENT">Installment</option>
                      <option value="CASH">Cash</option>
                      <option value="SPOT_CASH">Spot Cash</option>
                    </select>
                  </div>
                  {reserveForm.paymentType === 'INSTALLMENT' && (
                    <div>
                      <label htmlFor="lot-reserve-months" style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600 }}>Months</label>
                      <input id="lot-reserve-months" type="number" value={reserveForm.installmentMonths}
                        onChange={e => setReserveForm(f => ({ ...f, installmentMonths: e.target.value }))}
                        style={{
                          width: '100%', background: 'var(--input-bg)',
                          border: '1px solid var(--input-border)', borderRadius: '6px',
                          padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px',
                          fontFamily: 'var(--font-body)', outline: 'none',
                        }} />
                    </div>
                  )}
                </div>

                {/* Notes */}
                <input aria-label="Reservation notes" placeholder="Notes (optional)"
                  value={reserveForm.notes} onChange={e => setReserveForm(f => ({ ...f, notes: e.target.value }))}
                  style={{
                    width: '100%', background: 'var(--input-bg)',
                    border: '1px solid var(--input-border)', borderRadius: '6px',
                    padding: '8px 10px', color: 'var(--input-text)', fontSize: '13px',
                    fontFamily: 'var(--font-body)', outline: 'none',
                  }} />

                <Button variant="primary" size="sm" fullWidth icon="solar:hand-money-bold-duotone" onClick={handleReserve}>
                  Reserve Lot & Create Sale
                </Button>
              </>
            )}

            {/* ── RESERVED: Sale Details + Actions ─────── */}
            {lot.status === 'RESERVED' && txCtx?.sale && !txLoading && (
              <>
                <div style={{ fontSize: '13px', color: 'var(--warning)', fontWeight: 700, textTransform: 'none', letterSpacing: 'normal' }}>
                  Active Reservation
                </div>

                {/* Client card */}
                <div style={{
                  padding: '10px', borderRadius: '8px', background: 'rgba(245,158,11,0.08)',
                  border: '1px solid rgba(245,158,11,0.15)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <div style={{
                      width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(245,158,11,0.20)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon icon="solar:user-bold-duotone" width={14} style={{ color: '#F59E0B' }} />
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700 }}>
                        {purchaserName(txCtx.sale.client)}
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                        {txCtx.sale.client.phone} · Agent: {txCtx.sale.agent.name}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sale summary */}
                {[
                  ['Sale Amount', fmt(txCtx.sale.saleAmount)],
                  ['Down Payment', fmt(txCtx.sale.downPayment)],
                  ['Total Paid', fmt(txCtx.sale.totalPaid)],
                  ['Balance', fmt(txCtx.sale.balance)],
                  ['Payment Type', txCtx.sale.paymentType],
                  ['Stage', txCtx.sale.stage],
                ].map(([label, value], i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{label}</span>
                    <span style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>{value}</span>
                  </div>
                ))}

                {/* Payment progress */}
                {txCtx.sale.payments && txCtx.sale.payments.length > 0 && (
                  <>
                    <Divider spacing="4px" />
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'none', letterSpacing: 'normal' }}>
                      Payments ({txCtx.sale.payments.filter((p: any) => p.status === 'PAID').length}/{txCtx.sale.payments.length})
                    </div>
                    <div style={{
                      height: '6px', borderRadius: '3px', background: 'rgba(148,163,184,0.10)', overflow: 'hidden',
                    }}>
                      <div style={{
                        height: '100%', borderRadius: '3px',
                        width: `${Math.min(100, (txCtx.sale.totalPaid / txCtx.sale.saleAmount) * 100)}%`,
                        background: 'linear-gradient(90deg, #0D9488, #10B981)',
                        transition: 'width 0.3s ease',
                      }} />
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '13px', textAlign: 'right' }}>
                      {Math.round((txCtx.sale.totalPaid / txCtx.sale.saleAmount) * 100)}% collected
                    </div>
                  </>
                )}

                <Divider spacing="4px" />

                {/* Documents */}
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'none', letterSpacing: 'normal' }}>
                  Documents
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button type="button" onClick={() => api.documents.openSaleContract(txCtx.sale.id).catch(() => setTxError('Could not open contract'))}
                    style={{ flex: 1, background: 'none', border: 0, padding: 0 }}>
                    <div style={{
                      padding: '8px', borderRadius: '6px', background: 'rgba(13,148,136,0.08)',
                      border: '1px solid rgba(13,148,136,0.15)', cursor: 'pointer', textAlign: 'center',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                    }}>
                      <Icon icon="solar:document-bold-duotone" width={13} style={{ color: '#0D9488' }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#0D9488' }}>Contract</span>
                    </div>
                  </button>
                  <button type="button" onClick={() => api.documents.openSoa(txCtx.sale.id).catch(() => setTxError('Could not open statement of account'))}
                    style={{ flex: 1, background: 'none', border: 0, padding: 0 }}>
                    <div style={{
                      padding: '8px', borderRadius: '6px', background: 'rgba(99,102,241,0.08)',
                      border: '1px solid rgba(99,102,241,0.15)', cursor: 'pointer', textAlign: 'center',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                    }}>
                      <Icon icon="solar:bill-list-bold-duotone" width={13} style={{ color: '#6366F1' }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#6366F1' }}>SOA</span>
                    </div>
                  </button>
                </div>

                <Divider spacing="4px" />

                {txCtx.sale.balance > 0 && <p className="inventory-form-note">Record outstanding payments before completing the sale.</p>}
                {(txCtx.sale.totalPaid > 0 || txCtx.sale.downPayment > 0) && <p className="inventory-form-note">To cancel a reservation with payments, use the cancellation and refund workflow in Sales Pipeline.</p>}
                {/* Actions */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <Button variant="primary" size="sm" style={{ flex: 1 }}
                    icon="solar:check-circle-bold-duotone" onClick={handleCompleteSale}
                    disabled={txLoading || txCtx.sale.balance > 0 || txCtx.sale.payments?.some((payment: any) => ['PENDING', 'OVERDUE'].includes(payment.status))}>
                    Complete Sale
                  </Button>
                  <Button variant="danger" size="sm" style={{ flex: 1 }}
                    icon="solar:undo-left-bold-duotone" onClick={handleRelease}
                    disabled={txLoading || txCtx.sale.totalPaid > 0 || txCtx.sale.downPayment > 0}>
                    Release
                  </Button>
                </div>
              </>
            )}

            {/* ── SOLD: Sale Summary ──────────────────── */}
            {lot.status === 'SOLD' && txCtx?.sale && !txLoading && (
              <>
                <div style={{ fontSize: '13px', color: '#EF4444', fontWeight: 700, textTransform: 'none', letterSpacing: 'normal' }}>
                  Sale Completed
                </div>

                <div style={{
                  padding: '10px', borderRadius: '8px', background: 'rgba(239,68,68,0.06)',
                  border: '1px solid rgba(239,68,68,0.12)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Icon icon="solar:user-bold-duotone" width={16} style={{ color: '#EF4444' }} />
                    <div>
                      <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 700 }}>
                        {purchaserName(txCtx.sale.client)}
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                        {txCtx.sale.client.phone}
                      </div>
                    </div>
                  </div>
                </div>

                {[
                  ['Sale Amount', fmt(txCtx.sale.saleAmount)],
                  ['Total Paid', fmt(txCtx.sale.totalPaid)],
                  ['Balance', fmt(txCtx.sale.balance)],
                  ['Completed', txCtx.sale.completedDate ? formatDate(txCtx.sale.completedDate) : '—'],
                ].map(([label, value], i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{label}</span>
                    <span style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>{value}</span>
                  </div>
                ))}

                <Divider spacing="4px" />

                {/* Documents */}
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'none', letterSpacing: 'normal' }}>
                  Documents
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button type="button" onClick={() => api.documents.openSaleContract(txCtx.sale.id).catch(() => setTxError('Could not open contract'))}
                    style={{ flex: 1, background: 'none', border: 0, padding: 0 }}>
                    <div style={{
                      padding: '8px', borderRadius: '6px', background: 'rgba(13,148,136,0.08)',
                      border: '1px solid rgba(13,148,136,0.15)', cursor: 'pointer', textAlign: 'center',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                    }}>
                      <Icon icon="solar:document-bold-duotone" width={13} style={{ color: '#0D9488' }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#0D9488' }}>Contract</span>
                    </div>
                  </button>
                  <button type="button" onClick={() => api.documents.openSoa(txCtx.sale.id).catch(() => setTxError('Could not open statement of account'))}
                    style={{ flex: 1, background: 'none', border: 0, padding: 0 }}>
                    <div style={{
                      padding: '8px', borderRadius: '6px', background: 'rgba(99,102,241,0.08)',
                      border: '1px solid rgba(99,102,241,0.15)', cursor: 'pointer', textAlign: 'center',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                    }}>
                      <Icon icon="solar:bill-list-bold-duotone" width={13} style={{ color: '#6366F1' }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#6366F1' }}>SOA</span>
                    </div>
                  </button>
                </div>
              </>
            )}

            {/* No sale for non-available statuses */}
            {!['AVAILABLE', 'RESERVED', 'SOLD'].includes(lot.status) && (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>
                Lot is {STATUS_CONFIG[lot.status].label} — no active transaction
              </div>
            )}

            {/* RESERVED/SOLD but no sale found */}
            {(lot.status === 'RESERVED' || lot.status === 'SOLD') && !txCtx?.sale && !txLoading && (
              <div style={{ color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>
                No sale record found for this lot.
              </div>
            )}
          </div>
        )}

        {/* ACTIONS TAB */}
        {activeTab === 'actions' && (
          <fieldset className="lot-manage" disabled={actionSaving} style={{ display: 'flex', flexDirection: 'column', gap: '14px', border: 0, padding: 0, minWidth: 0 }}>
            <div className="lot-content-heading"><h3>Manage lot</h3><p>Update availability, pricing, and internal notes.</p></div>
            {actionError && <p role="alert" className="inventory-error">{actionError}</p>}
            {['RESERVED', 'SOLD'].includes(lot.status) && <p className="inventory-error">Manage this sale in the Transaction tab. Payments and ownership are linked to its status.</p>}
            {/* Status transitions */}
            <div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'none', letterSpacing: 'normal', marginBottom: '8px' }}>
                Change Status
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {transitions.map(s => {
                  const sc = STATUS_CONFIG[s];
                  return (
                    <button key={s} onClick={() => handleStatusChange(s)}
                      style={{
                        padding: '6px 12px', borderRadius: '6px', border: `1px solid ${sc.color}40`,
                        background: sc.bg, color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600,
                        cursor: 'pointer', fontFamily: 'var(--font-body)', display: 'flex',
                        alignItems: 'center', gap: '4px', transition: 'all 0.15s',
                      }}>
                      <Icon icon={sc.icon} width={12} />
                      {sc.label}
                    </button>
                  );
                })}
              </div>
              {transitions.length > 0 && (
                <div style={{ marginTop: '6px' }}>
                  <input
                    aria-label="Reason for status change" placeholder="Reason (optional)"
                    value={statusReason}
                    onChange={e => setStatusReason(e.target.value)}
                    style={{
                      width: '100%', background: 'var(--input-bg)',
                      border: '1px solid var(--input-border)', borderRadius: '6px',
                      padding: '6px 10px', color: 'var(--input-text)', fontSize: '13px',
                      fontFamily: 'var(--font-body)', outline: 'none',
                    }}
                  />
                </div>
              )}
            </div>

            <Divider spacing="4px" />

            {/* Price edit */}
            <div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'none', letterSpacing: 'normal', marginBottom: '8px' }}>
                Pricing
              </div>
              {showPriceEdit ? (
                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    aria-label="New listed price" type="number" placeholder={String(lot.price)} value={newPrice}
                    onChange={e => setNewPrice(e.target.value)}
                    style={{
                      flex: 1, background: 'var(--input-bg)',
                      border: '1px solid var(--input-border)', borderRadius: '6px',
                      padding: '6px 10px', color: 'var(--input-text)', fontSize: '13px',
                      fontFamily: 'var(--font-body)', outline: 'none',
                    }}
                  />
                  <Button variant="primary" size="sm" onClick={handlePriceSave}>Save</Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowPriceEdit(false)}>Cancel</Button>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: 'var(--text-primary)', fontSize: '14px', fontWeight: 600 }}>{fmt(lot.price)}</span>
                  <Button variant="ghost" size="sm" icon="solar:pen-bold-duotone"
                    onClick={() => { setNewPrice(String(lot.price)); setShowPriceEdit(true); }}>Edit</Button>
                </div>
              )}
            </div>

            <Divider spacing="4px" />

            {onShapeChange && section?.layoutMode !== 'GRID' && <section>
              <h3>Lot outline</h3>
              {showShapeEdit ? <>
                <PolygonEditor value={shapeDraft} onChange={setShapeDraft} onValidityChange={setShapeValid} width={lot.width || section?.lotWidth} height={lot.height || section?.lotHeight} disabled={actionSaving} />
                <div className="inventory-actions"><Button variant="primary" size="sm" disabled={!shapeValid} onClick={async()=>{if(await runAction(()=>onShapeChange(lot.id,shapeDraft)))setShowShapeEdit(false);}}>Save outline</Button><Button variant="secondary" size="sm" onClick={()=>setShowShapeEdit(false)}>Cancel</Button></div>
              </> : <><p className="lot-lock-help">{lot.points?.length ? `${lot.points.length}-point polygon` : 'Rectangular outline'}. Change the boundary without changing the sale or status.</p><Button variant="secondary" size="sm" onClick={()=>{setShapeDraft(lot.points || null);setShowShapeEdit(true);}}>Edit outline</Button></>}
            </section>}

            {/* Reposition (freeform only) */}
            {lot.isFreeform && onReposition && (
              <>
                <Divider spacing="4px" />
                <div>
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'none', letterSpacing: 'normal', marginBottom: '8px' }}>
                    Position & Size
                  </div>
                  {showPosEdit ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                        {(['x', 'y', 'w', 'h'] as const).map(k => (
                          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: '13px', fontWeight: 600, width: '14px' }}>
                              {k === 'x' ? 'X' : k === 'y' ? 'Y' : k === 'w' ? 'W' : 'H'}
                            </span>
                            <input aria-label={{x: 'X position', y: 'Y position', w: 'Width', h: 'Height'}[k]} type="number" value={posForm[k]}
                              onChange={e => setPosForm({ ...posForm, [k]: parseInt(e.target.value) || 0 })}
                              style={{
                                flex: 1, background: 'var(--input-bg)',
                                border: '1px solid var(--input-border)', borderRadius: '4px',
                                padding: '4px 6px', color: 'var(--input-text)', fontSize: '13px',
                                fontFamily: 'var(--font-code)', outline: 'none', width: '100%',
                              }} />
                          </div>
                        ))}
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <Button variant="primary" size="sm" onClick={async () => {
                          if (await runAction(() => onReposition(lot.id, { x_coord: posForm.x, y_coord: posForm.y, width: posForm.w, height: posForm.h }))) setShowPosEdit(false);
                        }}>Save</Button>
                        <Button variant="ghost" size="sm" onClick={() => setShowPosEdit(false)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '13px', fontFamily: 'var(--font-code)' }}>
                        ({Math.round(lot.x_coord)}, {Math.round(lot.y_coord)}) {lot.width && lot.height ? `${lot.width}×${lot.height}` : ''}
                      </span>
                      <Button variant="ghost" size="sm" icon="solar:pen-bold-duotone"
                        onClick={() => {
                          setPosForm({ x: Math.round(lot.x_coord), y: Math.round(lot.y_coord), w: lot.width || section?.lotWidth || 50, h: lot.height || section?.lotHeight || 50 });
                          setShowPosEdit(true);
                        }}>Edit</Button>
                    </div>
                  )}
                </div>
              </>
            )}

            <Divider spacing="4px" />

            {/* Lock/Unlock */}
            <div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'none', letterSpacing: 'normal', marginBottom: '8px' }}>
                Lock Management
              </div>
              <p className="lot-lock-help">{lot.isLocked ? 'Unlock this lot to restore its availability for transactions.' : 'Temporarily prevent new reservations without deleting this lot.'}</p>
              <Button variant="secondary" size="sm"
                icon={lot.isLocked ? 'solar:lock-open-bold-duotone' : 'solar:lock-bold-duotone'}
                onClick={() => runAction(() => onLockToggle(lot.id, !lot.isLocked, 'Manual toggle'))}>
                {lot.isLocked ? 'Unlock Lot' : 'Lock Lot'}
              </Button>
            </div>

            <Divider spacing="4px" />

            {/* Add note */}
            <div>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'none', letterSpacing: 'normal', marginBottom: '8px' }}>
                Add Note
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input
                  aria-label="Internal note" placeholder="Type a note..."
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddNote()}
                  style={{
                    flex: 1, background: 'var(--input-bg)',
                    border: '1px solid var(--input-border)', borderRadius: '6px',
                    padding: '6px 10px', color: 'var(--input-text)', fontSize: '13px',
                    fontFamily: 'var(--font-body)', outline: 'none',
                  }}
                />
                <Button variant="primary" size="sm" onClick={handleAddNote}>Add</Button>
              </div>
            </div>

            {/* Delete lot */}
            {onDelete && !['SOLD', 'RESERVED'].includes(lot.status) && (
              <>
                <Divider spacing="4px" />
                <div>
                  <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'none', letterSpacing: 'normal', marginBottom: '8px' }}>
                    Delete lot
                  </div>
                  <p className="lot-lock-help">Permanently remove this lot from inventory. This cannot be undone.</p>
                  <Button variant="danger" size="sm"
                    icon="solar:trash-bin-trash-bold-duotone"
                    onClick={() => onDelete(lot.id)}>
                    Delete Lot
                  </Button>
                </div>
              </>
            )}
          </fieldset>
        )}

        {activeTab === 'history' && <section className="lot-activity">
          <div className="lot-content-heading"><h3>Activity</h3><p>Recorded changes to this lot, with the person and reason behind each update.</p></div>
          {auditError ? <p role="alert" className="inventory-error">{auditError}</p> : lotLogs.length ? <ol className="lot-timeline">{lotLogs.map(log => <li key={log.id}>
            <Icon icon={historyIcon} width={20} />
            <div><div className="lot-timeline-heading"><strong>{log.action.replace(/_/g, ' ').toLowerCase()}</strong><time dateTime={log.createdAt}>{formatDate(log.createdAt)}</time></div>
              {(log.fromValue || log.toValue) && <p>{log.fromValue}{log.fromValue && log.toValue ? ' → ' : ''}{log.toValue}</p>}
              {log.reason && <p>{log.reason}</p>}
              {log.action === 'PROPERTY_DETAILS' && <PropertyCorrectionDetails log={log} />}
              {log.performedBy && <span>Updated by {log.metadata?.actorName || log.performedBy}</span>}
            </div>
          </li>)}</ol> : <div className="lot-activity-empty"><Icon icon={historyIcon} width={32} /><h4>No activity recorded yet</h4><p>Saved changes to status, price, locks, and notes will appear here.</p></div>}
        </section>}
        </div>
        <footer className="lot-sheet-footer"><span>Lot {lot.lotNumber} · {section?.label || 'Park inventory'}</span><Button variant="secondary" size="sm" onClick={requestClose} disabled={actionSaving || txSubmitting}>Back to inventory</Button></footer>
      </div>
    </dialog>, document.body
  );
};

export default LotDetailDrawer;
